import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, getAuthHeaders } from "../App";
import Layout from "../components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { LayoutDashboard, Plus, Trash2, GripVertical, Save, Loader2, Eye, EyeOff, Palette, BarChart3, AlertTriangle, Clock, FileText, Wrench, Package, Search, Bell, CheckCircle2 } from "lucide-react";
import { DynamicIcon } from "../components/shared/IconMap";
import { toast } from "sonner";
import { PageHeader } from "../components/shared/PageHeader";

const ICON_OPTIONS = ["FileText", "Wrench", "AlertTriangle", "Clock", "CheckCircle2", "Bell", "Package", "Search", "Activity", "BarChart3", "Shield", "Gauge", "Database", "Users", "Zap"];

export default function DashboardBuilderPage({ onLogout, userRole, isFeatureEnabled }) {
  const [config, setConfig] = useState(null);
  const [catalog, setCatalog] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [editTab, setEditTab] = useState(null);
  const [addKPIOpen, setAddKPIOpen] = useState(false);

  const fetchConfig = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/dashboard/config`, { headers: getAuthHeaders() });
      setConfig(resp.data);
      setCatalog(resp.data.widget_catalog || {});
    } catch { toast.error("Failed to load dashboard config"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/dashboard-config`, {
        tabs: config.tabs,
        kpis: config.kpis,
        panels: config.panels,
        bp_type_colors: config.bp_type_colors,
      }, { headers: getAuthHeaders() });
      toast.success("Dashboard configuration saved");
      setDirty(false);
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const update = (field, value) => { setConfig(c => ({ ...c, [field]: value })); setDirty(true); };

  // Tab handlers
  const addTab = () => {
    const key = `tab_${Date.now()}`;
    update("tabs", [...(config.tabs || []), { key, label: "New Tab", desc: "", bp_filter: "all" }]);
  };
  const updateTab = (i, field, val) => {
    const tabs = [...config.tabs];
    tabs[i] = { ...tabs[i], [field]: val };
    update("tabs", tabs);
  };
  const removeTab = (i) => update("tabs", config.tabs.filter((_, j) => j !== i));
  const moveTab = (i, dir) => {
    const tabs = [...config.tabs];
    const ni = i + dir;
    if (ni < 0 || ni >= tabs.length) return;
    [tabs[i], tabs[ni]] = [tabs[ni], tabs[i]];
    update("tabs", tabs);
  };

  // KPI handlers
  const addKPI = (sourceKey) => {
    const cat = catalog[sourceKey] || {};
    update("kpis", [...(config.kpis || []), { key: sourceKey, source: sourceKey, label: cat.label || sourceKey, icon: cat.icon || "Activity", color: cat.color || "#64748B" }]);
    setAddKPIOpen(false);
  };
  const removeKPI = (i) => update("kpis", config.kpis.filter((_, j) => j !== i));
  const updateKPI = (i, field, val) => {
    const kpis = [...config.kpis];
    kpis[i] = { ...kpis[i], [field]: val };
    update("kpis", kpis);
  };
  const moveKPI = (i, dir) => {
    const kpis = [...config.kpis];
    const ni = i + dir;
    if (ni < 0 || ni >= kpis.length) return;
    [kpis[i], kpis[ni]] = [kpis[ni], kpis[i]];
    update("kpis", kpis);
  };

  // Panel handlers
  const togglePanel = (i) => {
    const panels = [...config.panels];
    panels[i] = { ...panels[i], hidden: !panels[i].hidden };
    update("panels", panels);
  };

  if (loading || !config) return <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}><div className="flex items-center justify-center h-64"><Loader2 className="animate-spin" size={24} /></div></Layout>;

  const activeKPIKeys = new Set((config.kpis || []).map(k => k.source || k.key));
  const availableWidgets = Object.entries(catalog).filter(([k]) => !activeKPIKeys.has(k));

  return (
    <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}>
      <div className="space-y-6" data-testid="dashboard-builder-page">
        <div className="flex items-center justify-between">
          <PageHeader title="Dashboard Builder" subtitle="Configure tabs, KPI widgets, panels, and chart colors" testId="dashboard-builder-title" />
          <Button onClick={handleSave} disabled={saving || !dirty} className="bg-[#2563EB] text-white rounded-none" data-testid="save-dashboard-btn">
            {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : <Save size={14} className="mr-1" />} Save Configuration
          </Button>
        </div>

        {/* ─── TABS ─── */}
        <div className="bg-white border border-[#E2E8F0]" data-testid="tabs-builder">
          <div className="p-4 border-b border-[#E2E8F0] flex items-center justify-between bg-[#FAFAFA]">
            <h2 className="font-bold text-sm text-[#0F172A] flex items-center gap-2"><LayoutDashboard size={16} /> Dashboard Tabs ({(config.tabs || []).length})</h2>
            <Button size="sm" onClick={addTab} className="rounded-none bg-[#D97706] text-white text-xs" data-testid="add-tab-btn"><Plus size={12} className="mr-1" /> Add Tab</Button>
          </div>
          <div className="divide-y divide-[#E2E8F0]">
            {(config.tabs || []).map((tab, i) => (
              <div key={i} className="px-4 py-3 flex items-center gap-3" data-testid={`tab-row-${i}`}>
                <div className="flex flex-col gap-0.5">
                  <button onClick={() => moveTab(i, -1)} disabled={i === 0} className="text-[#94A3B8] hover:text-[#0F172A] disabled:opacity-30 text-xs">▲</button>
                  <button onClick={() => moveTab(i, 1)} disabled={i === config.tabs.length - 1} className="text-[#94A3B8] hover:text-[#0F172A] disabled:opacity-30 text-xs">▼</button>
                </div>
                <Input value={tab.key || ""} onChange={e => updateTab(i, "key", e.target.value)} className="w-28 rounded-none font-mono text-xs h-8" placeholder="key" />
                <Input value={tab.label || ""} onChange={e => updateTab(i, "label", e.target.value)} className="w-36 rounded-none text-xs h-8" placeholder="Label" />
                <Input value={tab.desc || ""} onChange={e => updateTab(i, "desc", e.target.value)} className="flex-1 rounded-none text-xs h-8" placeholder="Description" />
                <button onClick={() => removeTab(i)} className="text-[#DC2626] hover:bg-[#FEF2F2] p-1"><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        </div>

        {/* ─── KPI WIDGETS ─── */}
        <div className="bg-white border border-[#E2E8F0]" data-testid="kpi-builder">
          <div className="p-4 border-b border-[#E2E8F0] flex items-center justify-between bg-[#FAFAFA]">
            <h2 className="font-bold text-sm text-[#0F172A] flex items-center gap-2"><BarChart3 size={16} /> KPI Widgets ({(config.kpis || []).length})</h2>
            <Button size="sm" onClick={() => setAddKPIOpen(true)} disabled={availableWidgets.length === 0} className="rounded-none bg-[#D97706] text-white text-xs" data-testid="add-kpi-btn"><Plus size={12} className="mr-1" /> Add Widget</Button>
          </div>
          <div className="divide-y divide-[#E2E8F0]">
            {(config.kpis || []).map((kpi, i) => (
              <div key={i} className="px-4 py-2.5 flex items-center gap-3" data-testid={`kpi-row-${i}`}>
                <div className="flex flex-col gap-0.5">
                  <button onClick={() => moveKPI(i, -1)} disabled={i === 0} className="text-[#94A3B8] hover:text-[#0F172A] disabled:opacity-30 text-xs">▲</button>
                  <button onClick={() => moveKPI(i, 1)} disabled={i === (config.kpis || []).length - 1} className="text-[#94A3B8] hover:text-[#0F172A] disabled:opacity-30 text-xs">▼</button>
                </div>
                <div className="w-8 h-8 flex items-center justify-center" style={{ color: kpi.color || "#64748B" }}>
                  <DynamicIcon name={kpi.icon || "Activity"} size={20} />
                </div>
                <input type="color" value={kpi.color || "#64748B"} onChange={e => updateKPI(i, "color", e.target.value)} className="w-6 h-6 border-0 cursor-pointer" />
                <Input value={kpi.label || ""} onChange={e => updateKPI(i, "label", e.target.value)} className="flex-1 rounded-none text-xs h-8" />
                <select value={kpi.icon || "Activity"} onChange={e => updateKPI(i, "icon", e.target.value)} className="border border-[#E2E8F0] text-xs px-1 py-1 w-32">
                  {ICON_OPTIONS.map(ic => <option key={ic} value={ic}>{ic}</option>)}
                </select>
                <span className="text-[9px] font-mono text-[#94A3B8] w-32 truncate">{kpi.source || kpi.key}</span>
                <button onClick={() => removeKPI(i)} className="text-[#DC2626] hover:bg-[#FEF2F2] p-1"><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        </div>

        {/* ─── PANELS ─── */}
        <div className="bg-white border border-[#E2E8F0]" data-testid="panels-builder">
          <div className="p-4 border-b border-[#E2E8F0] bg-[#FAFAFA]">
            <h2 className="font-bold text-sm text-[#0F172A]">Dashboard Panels</h2>
          </div>
          <div className="divide-y divide-[#E2E8F0]">
            {(config.panels || []).map((panel, i) => (
              <div key={i} className="px-4 py-3 flex items-center justify-between" data-testid={`panel-row-${i}`}>
                <div className="flex items-center gap-3">
                  <span className={`text-sm ${panel.hidden ? "text-[#CBD5E1] line-through" : "text-[#0F172A] font-medium"}`}>{panel.label || panel.type}</span>
                  <span className="text-[9px] font-mono text-[#94A3B8]">{panel.type}</span>
                  {panel.roles && <span className="text-[9px] px-1.5 py-0.5 bg-[#FEF3C7] border border-[#F59E0B] text-[#92400E]">{panel.roles.join(", ")}</span>}
                </div>
                <button onClick={() => togglePanel(i)} className={panel.hidden ? "text-[#CBD5E1]" : "text-[#10B981]"} data-testid={`toggle-panel-${i}`}>
                  {panel.hidden ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* ─── CHART COLORS ─── */}
        <div className="bg-white border border-[#E2E8F0]" data-testid="colors-builder">
          <div className="p-4 border-b border-[#E2E8F0] bg-[#FAFAFA]">
            <h2 className="font-bold text-sm text-[#0F172A] flex items-center gap-2"><Palette size={16} /> Blueprint Type Chart Colors</h2>
          </div>
          <div className="p-4 grid grid-cols-2 md:grid-cols-3 gap-3">
            {Object.entries(config.bp_type_colors || {}).map(([key, val]) => (
              <div key={key} className="flex items-center gap-2 p-2 border border-[#E2E8F0]" data-testid={`color-${key}`}>
                <input type="color" value={val.color || "#64748B"} onChange={e => {
                  const colors = { ...config.bp_type_colors, [key]: { ...val, color: e.target.value } };
                  update("bp_type_colors", colors);
                }} className="w-8 h-8 border-0 cursor-pointer" />
                <div>
                  <p className="text-xs font-medium text-[#0F172A]">{val.label || key}</p>
                  <p className="text-[9px] font-mono text-[#94A3B8]">{key}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Add KPI Dialog */}
      <Dialog open={addKPIOpen} onOpenChange={setAddKPIOpen}>
        <DialogContent className="bg-white rounded-none border border-[#E2E8F0] max-w-sm" data-testid="add-kpi-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#0F172A]">Add KPI Widget</DialogTitle>
            <DialogDescription className="text-xs text-[#64748B]">Select a data source for the new widget.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1 max-h-64 overflow-y-auto">
            {availableWidgets.map(([key, meta]) => (
              <button key={key} onClick={() => addKPI(key)} className="w-full text-left px-3 py-2.5 border border-[#E2E8F0] hover:border-[#2563EB] hover:bg-[#EFF6FF] flex items-center gap-3 transition-colors" data-testid={`add-kpi-${key}`}>
                <DynamicIcon name={meta.icon || "Activity"} size={18} style={{ color: meta.color }} />
                <div><p className="text-sm font-medium text-[#0F172A]">{meta.label}</p><p className="text-[9px] font-mono text-[#94A3B8]">{key}</p></div>
              </button>
            ))}
            {availableWidgets.length === 0 && <p className="text-sm text-[#94A3B8] text-center py-4">All widgets are already on the dashboard.</p>}
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
